import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import { useApp } from '../context/AppContext';
import DashboardPage, { PAYMENT_PROVIDERS, PaymentProviderOption, PaymentProviderSelector, TurnoverInfo } from './DashboardPage';
import {
  calculatePublicOrderPayment,
  getPublicOrderPaymentLimits,
  PaymentCalculation,
  PaymentLimits,
} from '../api/api';
import { clientErrorMessage } from '../utils/clientError';

const featureStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const dotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--accent)',
  flexShrink: 0,
  boxShadow: '0 0 8px var(--accent)',
};

const featureTextStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--text-muted)',
};

const PUBLIC_MIN_PAYMENT_RUB = 10000;

function calculatedAssetAmount(calculation: PaymentCalculation, asset: PaymentProviderOption['asset']) {
  return asset === 'ETH' ? calculation.assetAmount ?? calculation.usdtAmount : calculation.usdtAmount;
}

export default function LandingPage() {
  const { userAuth, settings, refreshSettings } = useApp();
  const [params] = useSearchParams();
  const [rub, setRub] = useState('');
  const [provider, setProvider] = useState<PaymentProviderOption>(PAYMENT_PROVIDERS[0]);
  const [limits, setLimits] = useState<PaymentLimits | null>(null);
  const [calculation, setCalculation] = useState<PaymentCalculation | null>(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const referralCode = params.get('ref') || params.get('promo');
  const rubAmount = Number(rub) || 0;
  const minPaymentRub = limits?.minAmount ?? PUBLIC_MIN_PAYMENT_RUB;
  const maxPaymentRub = limits?.maxAmount;
  const availableLiquidity = settings?.liquidity ?? null;
  const hasLiquidity = availableLiquidity != null && availableLiquidity > 0;
  const calculatedAmount = calculation == null ? null : calculatedAssetAmount(calculation, provider.asset);
  const hasEnoughLiquidity = calculation != null &&
    availableLiquidity != null &&
    calculatedAmount != null &&
    calculatedAmount <= availableLiquidity;
  const amountIsWithinLimits = rubAmount >= minPaymentRub &&
    (maxPaymentRub == null || rubAmount <= maxPaymentRub);
  const registrationUrl = referralCode
    ? `/register?ref=${encodeURIComponent(referralCode)}`
    : '/register';

  useEffect(() => {
    refreshSettings({ asset: provider.asset, network: provider.network }).catch(() => {});
  }, [provider, refreshSettings]);

  useEffect(() => {
    let cancelled = false;
    getPublicOrderPaymentLimits()
      .then(result => { if (!cancelled) setLimits(result); })
      .catch(() => {
        if (!cancelled) {
          setLimits({ minAmount: PUBLIC_MIN_PAYMENT_RUB, maxAmount: null });
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!amountIsWithinLimits) {
      setCalculation(null);
      setCalculationLoading(false);
      return;
    }

    let cancelled = false;
    setCalculationLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const result = await calculatePublicOrderPayment({ rub: rubAmount, asset: provider.asset, network: provider.network });
        if (!cancelled) {
          setCalculation(result);
          setCalculationError('');
        }
      } catch (err) {
        if (!cancelled) {
          setCalculation(null);
          setCalculationError(clientErrorMessage(err, 'Не удалось рассчитать сумму.'));
        }
      } finally {
        if (!cancelled) setCalculationLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [amountIsWithinLimits, provider, rubAmount]);

  if (!userAuth.token && referralCode) {
    return <Navigate to={`/register?ref=${encodeURIComponent(referralCode)}`} replace />;
  }

  if (userAuth.token) return <DashboardPage />;

  return (
    <div className="landing-layout">
      <div className="landing-left">
        <div className="brand"><span className="brand-dot"></span><span className="brand-name">daotta</span></div>
        <h1 className="landing-headline">Покупка ETH<br /><span className="accent">через СБП</span></h1>
        <p className="landing-sub">Быстрая оплата в рублях.<br />Статус платежа доступен в кабинете.</p>
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Выберите ETH или USDT в BNB Smart Chain</span></div>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Пройдите KYC перед первым платежом</span></div>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Следите за заявками в личном кабинете</span></div>
        </div>
      </div>
      <div className="landing-right">
        <Card>
          <TurnoverInfo turnover={settings?.turnover ?? null} />
          <div className="public-payment-preview">
            <div>
              <h2 className="success-title public-payment-title">Рассчитайте покупку</h2>
              <p className="success-sub public-payment-sub">
                Введите сумму и узнайте, сколько {provider.asset} получите на daotta.
              </p>
            </div>
            <PaymentProviderSelector value={provider} onChange={setProvider} />
            <Input
              label="Сумма в рублях"
              type="number"
              min={minPaymentRub?.toString()}
              max={maxPaymentRub?.toString()}
              value={rub}
              onChange={event => setRub(event.target.value)}
              suffix="₽"
            />
            {rubAmount > 0 && rubAmount < minPaymentRub ? (
              <div className="public-payment-hint">
                Минимальная сумма оплаты {minPaymentRub.toLocaleString('ru-RU')} ₽
              </div>
            ) : null}
            {maxPaymentRub != null && rubAmount > maxPaymentRub ? (
              <div className="public-payment-hint">
                Максимальная сумма оплаты {maxPaymentRub.toLocaleString('ru-RU')} ₽
              </div>
            ) : null}
            {amountIsWithinLimits ? (
              <div className="calc-result">
                <span className="calc-label">Получите</span>
                <span className="calc-amount mono">
                  {calculationLoading || calculatedAmount == null ? '...' : `${calculatedAmount.toFixed(6)} ${provider.asset}`}
                </span>
              </div>
            ) : null}
            {availableLiquidity != null && !hasLiquidity ? (
              <div className="public-payment-error">Нет доступной ликвидности {provider.asset}.</div>
            ) : null}
            {hasLiquidity && calculation && !hasEnoughLiquidity ? (
              <div className="public-payment-error">Недостаточно ликвидности {provider.asset}.</div>
            ) : null}
            {calculationError ? <div className="public-payment-error">{calculationError}</div> : null}
            <Link to={registrationUrl}><Button fullWidth size="lg">Зарегистрироваться</Button></Link>
            <Link to="/login" className="public-login-link">Уже есть аккаунт? Войти</Link>
          </div>
        </Card>
        <div className="legal-info-block">
          Покупка ETH через СБП осуществляется с использованием инфраструктуры лицензированного поставщика услуг виртуальных активов Bitbanker KG (Кыргызская Республика). Прохождение идентификации (KYC) является обязательным условием использования сервиса.
        </div>
      </div>
    </div>
  );
}
